import { Dialog } from 'components/dialogs/Dialog'
import { observer } from 'mobx-react-lite'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import type { FirestoreRoomStateSummary } from '@breakoutlearning/firebase-repository/types'
import { LazyMarkdown } from 'components/LazyMarkdown'
import { useEffect, memo } from 'react'
import { useRepository } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { GraphBarIcon } from 'components/icons/GraphBar'
import { DateTime } from 'luxon'
import { LightBulbIcon } from 'components/icons/LightBulb'
import classNames from 'classnames'

const FinishedAt = memo(function FinishedAt({
  timestamp,
}: {
  timestamp: Date | null
}) {
  const { tt } = useTranslationTyped()
  if (!timestamp) return null

  return (
    <div className="text-right text-body-medium">
      {tt.meeting.results.finished_at({
        finished_at: DateTime.fromJSDate(timestamp).toLocaleString(
          DateTime.DATETIME_FULL
        ),
      })}
    </div>
  )
})

const Hint = memo(function Hint({
  text,
  className,
}: {
  className?: string
  text: string
}) {
  return (
    <div
      className={classNames(
        'flex flex-row items-center border-t border-medium-grey pt-3 text-grey-text',
        className
      )}
    >
      <div className="mx-1 flex-none">
        <LightBulbIcon size={15} />
      </div>
      <div className="line-clamp-3 text-body-small">{text}</div>
    </div>
  )
})

const SummaryDecoration = observer(function SummaryWithDecoration({
  roomStateActiveSlideChangedAt,
  roomStateName,
  children,
}: {
  roomStateActiveSlideChangedAt: Date | null
  roomStateName: string | null
  children: React.ReactNode
}) {
  const { tt } = useTranslationTyped()
  return (
    <div className="flex flex-1 flex-col justify-between rounded-2xl">
      <div>
        <div className="flex flex-row items-center p-5">
          <GraphBarIcon className="mr-1" color="green" size={15} />
          <strong className="text-label-small">
            {tt.summary.ai_summary()}
          </strong>
        </div>
        <div className="flex flex-row justify-between px-10">
          <div className="text-label-large">{roomStateName}</div>
          <FinishedAt timestamp={roomStateActiveSlideChangedAt} />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-start overflow-auto px-10">
        {/* summary view place children here*/}
        {children}
      </div>
      <Hint className="mx-10 mt-6 pb-6" text={tt.summary.summary_hint()} />
    </div>
  )
})

export const SummaryDialog = observer(function SummaryDialog({
  summary,
  roomStateActiveSlideChangedAt,
  roomStateName,
}: {
  summary: FirestoreRoomStateSummary
  roomStateActiveSlideChangedAt: Date | null
  roomStateName: string
}) {
  const repository = useRepository()
  const { tt } = useTranslationTyped()
  useEffect(() => {
    repository.logEvent('room_view_summary')
  }, [repository])

  return (
    <Dialog
      size="lg"
      className="flex flex-col justify-start overflow-hidden bg-core-secondary"
      aria-labelledby={undefined}
      aria-label={tt.student_assignment.view_summary()}
    >
      <DialogCloseButton className="absolute right-0 top-0 p-5 pb-0" />
      <div className="h-full overflow-auto">
        <SummaryDecoration
          roomStateActiveSlideChangedAt={roomStateActiveSlideChangedAt}
          roomStateName={roomStateName}
        >
          <div className="react-markdown mt-5">
            <LazyMarkdown>{summary.summary}</LazyMarkdown>
          </div>
        </SummaryDecoration>
      </div>
    </Dialog>
  )
})
